import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Check, X, RefreshCw } from 'lucide-react';
import { traditionsMode } from '../../data/toolsData';

const TraditionsTool = () => {
  const navigate = useNavigate();
  const [choices, setChoices] = useState({});

  const handleChoice = (itemId, choice) => {
    setChoices(prev => ({ ...prev, [itemId]: choice }));
  };

  const getChoiceColor = (choice) => {
    switch(choice) {
      case 'follow': return '#B5C4B1';
      case 'adapt': return '#D4C4B0';
      case 'ignore': return '#E8D5D5';
      default: return '#E8E4E0';
    }
  };

  const getStats = () => {
    const values = Object.values(choices);
    return {
      follow: values.filter(v => v === 'follow').length,
      adapt: values.filter(v => v === 'adapt').length,
      ignore: values.filter(v => v === 'ignore').length,
      total: values.length
    };
  };

  const stats = getStats();

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/ferramentas')}
          className="flex items-center gap-2 text-[#8B7D6B] hover:text-[#6B4E37] mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          Voltar às ferramentas
        </button>

        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-[#B5C4B1]/30 flex items-center justify-center mx-auto mb-6">
            <Sparkles size={32} className="text-[#6B4E37]" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            {traditionsMode.title}
          </h1>
          <p className="text-lg text-[#6B4E37] max-w-xl mx-auto">
            {traditionsMode.subtitle}
          </p>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          <div className="flex items-center gap-2 px-4 py-2 bg-[#B5C4B1]/20 rounded-full">
            <Check size={16} className="text-[#B5C4B1]" />
            <span className="text-sm text-[#6B4E37]">Seguir</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-[#D4C4B0]/20 rounded-full">
            <RefreshCw size={16} className="text-[#D4C4B0]" />
            <span className="text-sm text-[#6B4E37]">Adaptar</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-[#E8D5D5]/20 rounded-full">
            <X size={16} className="text-[#E8D5D5]" />
            <span className="text-sm text-[#6B4E37]">Ignorar</span>
          </div>
        </div>

        {/* Traditions by Category */}
        <div className="space-y-8">
          {traditionsMode.traditions.map((category) => (
            <div key={category.id} className="bg-white/60 backdrop-blur-sm rounded-2xl p-6">
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C] mb-6">
                {category.category}
              </h3>
              
              <div className="space-y-4">
                {category.items.map((item) => {
                  const currentChoice = choices[item.id];
                  
                  return (
                    <div 
                      key={item.id}
                      className="bg-white/80 rounded-xl p-5 shadow-sm"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <h4 className="font-medium text-[#2C2C2C] mb-1">{item.name}</h4>
                          <p className="text-sm text-[#8B7D6B]">{item.description}</p>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleChoice(item.id, 'follow')}
                            className={`p-3 rounded-lg transition-all duration-300 ${
                              currentChoice === 'follow'
                                ? 'bg-[#B5C4B1] text-white shadow-md'
                                : 'bg-[#F5F3F0] text-[#8B7D6B] hover:bg-[#B5C4B1]/20'
                            }`}
                            title="Seguir"
                          >
                            <Check size={18} />
                          </button>
                          <button
                            onClick={() => handleChoice(item.id, 'adapt')}
                            className={`p-3 rounded-lg transition-all duration-300 ${
                              currentChoice === 'adapt'
                                ? 'bg-[#D4C4B0] text-white shadow-md'
                                : 'bg-[#F5F3F0] text-[#8B7D6B] hover:bg-[#D4C4B0]/20'
                            }`}
                            title="Adaptar"
                          >
                            <RefreshCw size={18} />
                          </button>
                          <button
                            onClick={() => handleChoice(item.id, 'ignore')}
                            className={`p-3 rounded-lg transition-all duration-300 ${
                              currentChoice === 'ignore'
                                ? 'bg-[#E8D5D5] text-[#6B4E37] shadow-md'
                                : 'bg-[#F5F3F0] text-[#8B7D6B] hover:bg-[#E8D5D5]/20'
                            }`}
                            title="Ignorar"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </div>

                      {/* Show selected option description */}
                      {currentChoice && (
                        <div 
                          className="mt-4 p-3 rounded-lg text-sm animate-fade-in"
                          style={{ backgroundColor: `${getChoiceColor(currentChoice)}20` }}
                        >
                          <span className="text-[#6B4E37]">{item.options[currentChoice]}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Summary Stats */}
        {stats.total > 0 && (
          <div className="mt-10 bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-center animate-fade-in">
            <h3 className="text-sm font-medium text-[#8B7D6B] uppercase tracking-wider mb-4">
              O vosso perfil de tradições
            </h3>
            <div className="flex justify-center gap-8">
              <div>
                <div className="text-3xl font-light text-[#B5C4B1]">{stats.follow}</div>
                <div className="text-xs text-[#8B7D6B]">Seguir</div>
              </div>
              <div>
                <div className="text-3xl font-light text-[#D4C4B0]">{stats.adapt}</div>
                <div className="text-xs text-[#8B7D6B]">Adaptar</div>
              </div>
              <div>
                <div className="text-3xl font-light text-[#E8D5D5]">{stats.ignore}</div>
                <div className="text-xs text-[#8B7D6B]">Ignorar</div>
              </div>
            </div>
            
            {stats.total >= 5 && (
              <p className="mt-4 text-[#6B4E37]">
                {stats.follow > stats.adapt && stats.follow > stats.ignore && 
                  "Valorizam a tradição e a estrutura clássica. O vosso casamento terá uma elegância atemporal."}
                {stats.adapt > stats.follow && stats.adapt > stats.ignore && 
                  "Gostam de personalizar sem romper totalmente. Um equilíbrio entre respeito e autenticidade."}
                {stats.ignore > stats.follow && stats.ignore > stats.adapt && 
                  "Preferem criar o vosso próprio caminho. O vosso casamento será genuinamente único."}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TraditionsTool;
